/**
 * SiteHeader — the site-defining facts, pinned at the top of the E2 detail
 * (not in an accordion). Shows the building shell + the operating schedule E2
 * actually used, with a badge for where that schedule came from (the client's
 * own schedule, a ComStock archetype, or the generic default). When the
 * analysis carries provenance, that badge becomes a provenance marker
 * (PROD-10693). The caption
 * states what the window controls -- it is the number's biggest lever, and a
 * reader who knows that can spot a wrong window immediately. Fit / training-window / gas
 * groups are intentionally omitted here — this block is "what site is this".
 */
import type React from "react"
import { useTranslation } from "react-i18next"

import { Box, Chip, Stack, Typography } from "@mui/material"

import { ProvenanceLegend, ProvenanceMarker } from "./provenance-marker"
import type {
  FactKey,
  Provenance,
  SiteMetaSchedule,
  SiteMetadata,
} from "./schedule-misalignment.types"

const fmtHour = (h: number): string => {
  const hr = Math.floor(h)
  const min = Math.round((h - hr) * 60)
  const ampm = hr < 12 || hr === 24 ? "AM" : "PM"
  const h12 = hr % 12 || 12
  return min
    ? `${h12}:${String(min).padStart(2, "0")} ${ampm}`
    : `${h12} ${ampm}`
}

const hoursText = (win?: [number, number] | null): string =>
  win ? `${fmtHour(win[0])} – ${fmtHour(win[1])}` : "Closed"

const scheduleRows = (
  s: SiteMetaSchedule
): { day: string; hours: string }[] => [
  { day: "Mon–Fri", hours: hoursText(s.weekday) },
  { day: "Sat", hours: hoursText(s.saturday) },
  { day: "Sun", hours: hoursText(s.sunday) },
]

const SOURCE_BADGE: Record<
  SiteMetaSchedule["source"],
  { label: string; color: "success" | "info" | "default" }
> = {
  defined: { label: "From your schedule", color: "success" },
  comstock_archetype: { label: "ComStock archetype", color: "info" },
  generic: { label: "Default", color: "default" },
}

// Source copy for the schedule marker's tooltip, as `neat.provenance.*` keys.
// A defined schedule only reads "your schedule" when the label says confirmed;
// any other label on a defined schedule must not claim the customer supplied
// it. A fallback schedule (archetype or generic) names what it fell back to,
// because "on file for this site" would be wrong for it.
const scheduleDetailKey = (
  source: SiteMetaSchedule["source"],
  label: Provenance
): string => {
  switch (source) {
    case "defined":
      return label === "confirmed"
        ? "neat.provenance.scheduleSource.yours"
        : "neat.provenance.scheduleSource.onFile"
    case "comstock_archetype":
      return "neat.provenance.scheduleSource.archetype"
    default:
      return "neat.provenance.scheduleSource.generic"
  }
}

const FACTS: { key: FactKey; label: string; unit?: string }[] = [
  { key: "building_type", label: "Type" },
  { key: "climate_zone", label: "Climate zone" },
  { key: "floor_area_sqft", label: "Floor area", unit: "sq ft" },
  { key: "vintage", label: "Vintage" },
  { key: "hvac_system_type", label: "HVAC" },
  { key: "heating_fuel", label: "Heating" },
  { key: "rate_schedule", label: "Rate schedule" },
]

const Fact: React.FC<{
  label: string
  value: React.ReactNode
  marker?: React.ReactNode
}> = ({ label, value, marker }) => (
  <Stack data-fact spacing={0.25}>
    <Typography
      sx={{
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
      variant="caption"
    >
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 700 }} variant="body2">
      {value}
    </Typography>
    {marker}
  </Stack>
)

export const SiteHeader: React.FC<{ metadata: SiteMetadata }> = ({
  metadata,
}) => {
  const { t } = useTranslation("common")
  const b = metadata.building
  const s = metadata.schedule
  const prov = b?.provenance ?? null
  const facts = b
    ? FACTS.map((f) => ({ ...f, value: b[f.key] })).filter(
        (f) =>
          (f.value !== null && f.value !== undefined) ||
          // With provenance, a labelled null shows as "—" + Missing.
          prov?.[f.key] !== undefined
      )
    : []

  if (facts.length === 0 && !s) {
    return null
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "grey.200",
        borderLeft: 4,
        borderLeftColor: "primary.main",
        borderRadius: 2,
        bgcolor: "background.paper",
        p: 2.5,
      }}
    >
      {facts.length > 0 ? (
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 3, rowGap: 1.5 }}>
          {facts.map((f) => {
            const p = prov?.[f.key]
            return (
              <Fact
                key={f.key}
                label={f.label}
                marker={p ? <ProvenanceMarker provenance={p} /> : undefined}
                value={
                  f.value === null ||
                  f.value === undefined ||
                  (typeof f.value === "string" && f.value.trim() === "")
                    ? "—"
                    : f.key === "floor_area_sqft"
                      ? `${Number(f.value).toLocaleString()} ${f.unit}`
                      : f.key === "rate_schedule"
                        ? String(f.value).toUpperCase()
                        : String(f.value)
                }
              />
            )
          })}
        </Stack>
      ) : null}

      {s ? (
        <Box
          sx={{
            mt: facts.length > 0 ? 2 : 0,
            pt: facts.length > 0 ? 2 : 0,
            borderTop: facts.length > 0 ? 1 : 0,
            borderColor: "grey.100",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ alignItems: "center", mb: 1 }}
          >
            <Typography
              sx={{
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
              variant="caption"
            >
              Operating schedule
            </Typography>
            {prov?.occupancy_schedule ? (
              <ProvenanceMarker
                detail={t(scheduleDetailKey(s.source, prov.occupancy_schedule))}
                provenance={prov.occupancy_schedule}
              />
            ) : (
              <Chip
                color={SOURCE_BADGE[s.source].color}
                label={SOURCE_BADGE[s.source].label}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
          {s.always_on ? (
            <Typography sx={{ fontWeight: 700 }} variant="body2">
              Always on (24/7)
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {scheduleRows(s).map((r) => (
                <Stack direction="row" key={r.day} spacing={2}>
                  <Typography
                    sx={{ color: "text.secondary", minWidth: 72 }}
                    variant="body2"
                  >
                    {r.day}
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }} variant="body2">
                    {r.hours}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
          <Typography
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
            variant="caption"
          >
            Load outside these hours counts as after-hours potential savings.
            Tell us if they&rsquo;re wrong and the figures update.
          </Typography>
        </Box>
      ) : null}

      {prov ? (
        <Box sx={{ mt: 1.5 }}>
          <ProvenanceLegend />
        </Box>
      ) : null}
    </Box>
  )
}
